import { toast as sonnerToast, type ExternalToast } from 'sonner';

/**
 * Toast display durations — keep in sync with Apple ToastDuration
 * and Android *_AUTO_DISMISS_DELAY_MS constants.
 */
const SUCCESS_DURATION = 4_000;
const INFO_DURATION = 6_000;
const WARNING_DURATION = 10_000;
const ERROR_DURATION = 30_000;
const ACTION_DURATION = 15_000; // Both Action and Undoable.
// Tappable job-completion toasts (render / estimate / album ready): long
// enough to notice and tap, but shorter than Action — the artifact is also
// reachable from the page, so the toast doesn't need to linger.
const COMPLETION_DURATION = 6_000;

// Accumulated undo actions for undoable toasts that replace each other
const UNDO_TOAST_ID = 'undoable-action';
let accumulatedUndoActions: (() => void)[] = [];

export const toast = {
  success: (message: string | React.ReactNode, data?: ExternalToast) => {
    return sonnerToast.success(message, {
      duration: SUCCESS_DURATION,
      ...data,
    });
  },

  error: (message: string | React.ReactNode, data?: ExternalToast) => {
    return sonnerToast.error(message, {
      duration: ERROR_DURATION,
      ...data,
    });
  },

  warning: (message: string | React.ReactNode, data?: ExternalToast) => {
    return sonnerToast.warning(message, {
      duration: WARNING_DURATION,
      ...data,
    });
  },

  info: (message: string | React.ReactNode, data?: ExternalToast) => {
    return sonnerToast.info(message, {
      duration: INFO_DURATION,
      ...data,
    });
  },

  /**
   * Show a job-completion toast (render / estimate / album ready). The whole
   * body is tappable when `onTap` is provided (no inline button) — clicking
   * navigates to the finished artifact. Duration mirrors Apple ToastManager
   * and Android GlobalSnackbarHost: tappable completions live for
   * COMPLETION_DURATION (6 s, long enough to notice and tap), non-tappable
   * ones fall back to the plain SUCCESS_DURATION (4 s). Do NOT hardcode a
   * longer duration here — a success toast must never outlive an error toast.
   */
  completion: (
    message: string | React.ReactNode,
    data?: { description?: string; onTap?: () => void }
  ) => {
    const onTap = data?.onTap;
    return sonnerToast.success(message, {
      duration: onTap ? COMPLETION_DURATION : SUCCESS_DURATION,
      ...(data?.description && { description: data.description }),
      ...(onTap && {
        style: { cursor: 'pointer' },
        onClick: onTap,
      }),
    });
  },

  /**
   * Show undoable action toast. Multiple calls replace the previous toast
   * and combine all undo actions — pressing Undo undoes everything.
   */
  undoable: (
    message: string | React.ReactNode,
    data: ExternalToast & {
      action: { label: string; onClick: () => void };
    }
  ) => {
    const originalOnClick = data.action.onClick;
    accumulatedUndoActions.push(originalOnClick);
    const allActions = [...accumulatedUndoActions];

    return sonnerToast.success(message, {
      duration: ACTION_DURATION,
      ...data,
      // Per Material 3 / Apple HIG parity: a snackbar/toast carries at most
      // one affordance — either the action button or a dismiss icon, not both.
      // Undoable toasts always have an action; rely on action + auto-dismiss
      // (15 s) + swipe instead of stacking a redundant X next to "Undo".
      closeButton: false,
      id: UNDO_TOAST_ID,
      action: {
        label: data.action.label,
        onClick: () => {
          for (const action of allActions) action();
          accumulatedUndoActions = [];
        },
      },
      onDismiss: () => {
        accumulatedUndoActions = [];
      },
      onAutoClose: () => {
        accumulatedUndoActions = [];
      },
    });
  },

  // Pass through other toast methods
  message: sonnerToast.message,
  promise: sonnerToast.promise,
  custom: sonnerToast.custom,
  dismiss: sonnerToast.dismiss,
  loading: sonnerToast.loading,
};
